import { useEffect, useState } from 'react';
import type { StorageContainerAcl, StorageOperationError } from '../../../../../types/azureStorage';

type AclQuery<A> = { data?: A; reload: () => void };

/**
 * Draft + baseline of an ACL dialog (review C F2 class, review D RD3). The draft stays coupled to the ACL it was built
 * from: a refresh replaces an unchanged draft only, and every save is conditioned on that baseline. A rejected save
 * (conflict), an unknown outcome and a save whose re-read failed make the version this dialog knows STALE: draft and
 * baseline are kept, saving is blocked, and only `reload()` — an explicit action that discards a changed draft — adopts
 * the server's current ACL. An unchanged draft (e.g. the one just saved) is still replaced by the next successful read.
 * `A` is the ACL shape: a container's, or a table's / queue's policies (follow-up 2026-09-25).
 */
export function useAclDraft<T, A = StorageContainerAcl>(acl: AclQuery<A>, pick: (value: A) => T, same: (left: T, right: T) => boolean) {
  const [baseline, setBaseline] = useState<A>();
  const [draft, setDraft] = useState<T>();
  const [stale, setStale] = useState<string>();
  const dirty = !!baseline && draft !== undefined && !same(draft, pick(baseline));
  useEffect(() => {
    if (!acl.data || dirty) return;
    setBaseline(acl.data);
    setDraft(pick(acl.data));
    setStale(undefined);
  }, [acl.data]); // eslint-disable-line react-hooks/exhaustive-deps -- a refresh must not re-run on local edits
  return {
    baseline, draft, setDraft, dirty, stale,
    /** True when the failure leaves the known version stale (the caller shows any other error itself). */
    markFailed: (error: StorageOperationError): boolean => {
      if (error.category !== 'conflict' && error.category !== 'ambiguous_outcome') return false;
      setStale(error.message);
      return true;
    },
    /** The write landed but its re-read failed: the saved value is the new baseline content, its version is unknown. */
    markSavedWithoutReread: (saved: A) => { setBaseline(saved); setStale('Saved · could not re-read'); },
    reload: () => { setStale(undefined); setBaseline(undefined); acl.reload(); },
  };
}
